/**
 * Bicimlendirme — TEK YER.
 *
 * Tarih bicimini her sayfada tekrar yazmak, kacinilmaz olarak farkli sayfalarda
 * farkli bicimler uretir ("14.08.2026" / "14 Ağu 2026" / "2026-08-14").
 */

const TZ = 'Europe/Istanbul';
let language: 'tr' | 'en' = 'tr';
let locale = 'tr-TR';

let dateTimeFmt = new Intl.DateTimeFormat(locale, {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

let dateFmt = new Intl.DateTimeFormat(locale, {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

let timeFmt = new Intl.DateTimeFormat(locale, {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
});

let weekdayFmt = new Intl.DateTimeFormat(locale, { timeZone: TZ, weekday: 'long' });

export function formatDateTime(iso: string | null): string {
  return iso ? dateTimeFmt.format(new Date(iso)) : '—';
}

export function formatDate(iso: string | null): string {
  return iso ? dateFmt.format(new Date(iso)) : '—';
}

export function formatTime(iso: string | null): string {
  return iso ? timeFmt.format(new Date(iso)) : '—';
}

export function formatWeekday(iso: string): string {
  return weekdayFmt.format(new Date(iso));
}

/**
 * "3 dakika önce" / "2 gün sonra".
 *
 * Mutlak tarih her zaman daha kesin ama "en son ne zaman gorundu" gibi
 * sorularda goreli zaman ANINDA anlasilir. Ikisi birlikte kullanilir:
 * goreli metin gorunur, mutlak tarih `title` ozniteliginde durur.
 */
let relativeFmt = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

export function formatRelative(iso: string | null, now = Date.now()): string {
  if (!iso) return '—';
  const diffSec = (new Date(iso).getTime() - now) / 1000;
  const abs = Math.abs(diffSec);

  // 45 saniyeden yakinsa "az önce": saniye saymak gereksiz hassasiyet.
  if (abs < 45) return language === 'tr' ? 'az önce' : 'just now';

  for (const [unit, seconds] of UNITS) {
    if (abs >= seconds) {
      return relativeFmt.format(Math.round(diffSec / seconds), unit);
    }
  }
  return language === 'tr' ? 'az önce' : 'just now';
}

/** Bayt -> "1,4 GB". Binlik ayirici Turkce (nokta/virgul) kurallarina uyar. */
export function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toLocaleString(locale, { maximumFractionDigits: value < 10 ? 1 : 0 })} ${units[i]}`;
}

export function formatNumber(value: number): string {
  return value.toLocaleString(locale);
}

/** 90 -> "1 sa 30 dk" */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (language === 'en') {
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} hr`;
    return `${h} hr ${m} min`;
  }
  if (h === 0) return `${m} dk`;
  if (m === 0) return `${h} sa`;
  return `${h} sa ${m} dk`;
}

/** `<input type="datetime-local">` icin yerel saat dizesi. */
export function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ─── Etiketler ───────────────────────────────────────────────────────────────

export const PRODUCT_KIND_LABEL: Record<string, string> = {
  'vr-game': 'VR Deneyimi',
  'immersive-art': 'İmmersif Sanat',
  'motion-track': 'Hareket Takibi',
  installation: 'Enstalasyon',
  'led-service': 'LED Servisi',
  tool: 'Araç',
};

export const ENDPOINT_KIND_LABEL: Record<string, string> = {
  'led-screen': 'LED Ekran',
  'vr-station': 'VR İstasyonu',
  projection: 'Projeksiyon',
  installation: 'Enstalasyon',
  generic: 'Genel',
};

export const SESSION_STATUS_LABEL: Record<string, string> = {
  scheduled: 'Planlandı',
  running: 'Çalışıyor',
  completed: 'Tamamlandı',
  stopped: 'Durduruldu',
  failed: 'Başarısız',
  cancelled: 'İptal edildi',
};

export const CREDIT_REASON_LABEL: Record<string, string> = {
  grant: 'Paket yüklemesi',
  spend: 'Oturum',
  refund: 'İade',
  adjust: 'Düzeltme',
  expire: 'Süre doldu',
};

const EN_PRODUCT_KIND_LABEL: Record<string, string> = {
  'vr-game': 'VR Game',
  'immersive-art': 'Immersive Art',
  'motion-track': 'Motion Tracking',
  installation: 'Installation',
  'led-service': 'LED Service',
  tool: 'Tool',
};

const EN_ENDPOINT_KIND_LABEL: Record<string, string> = {
  'led-screen': 'LED Display',
  'vr-station': 'VR Station',
  projection: 'Projection',
  installation: 'Installation',
  generic: 'General',
};

const EN_SESSION_STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  running: 'Running',
  completed: 'Completed',
  stopped: 'Stopped',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const EN_CREDIT_REASON_LABEL: Record<string, string> = {
  grant: 'Package grant',
  spend: 'Session',
  refund: 'Refund',
  adjust: 'Adjustment',
  expire: 'Expired',
};

const TR_PRODUCT_KIND_LABEL = { ...PRODUCT_KIND_LABEL };
const TR_ENDPOINT_KIND_LABEL = { ...ENDPOINT_KIND_LABEL };
const TR_SESSION_STATUS_LABEL = { ...SESSION_STATUS_LABEL };
const TR_CREDIT_REASON_LABEL = { ...CREDIT_REASON_LABEL };

/** Dil degisince tum tarih/sayi bicimleri ve paylasilan durum etiketleri birlikte yenilenir. */
export function setFormatLanguage(value: 'tr' | 'en'): void {
  language = value;
  locale = value === 'tr' ? 'tr-TR' : 'en-US';
  dateTimeFmt = new Intl.DateTimeFormat(locale, { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  dateFmt = new Intl.DateTimeFormat(locale, { timeZone: TZ, day: '2-digit', month: 'short', year: 'numeric' });
  timeFmt = new Intl.DateTimeFormat(locale, { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  weekdayFmt = new Intl.DateTimeFormat(locale, { timeZone: TZ, weekday: 'long' });
  relativeFmt = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  Object.assign(PRODUCT_KIND_LABEL, value === 'tr' ? TR_PRODUCT_KIND_LABEL : EN_PRODUCT_KIND_LABEL);
  Object.assign(ENDPOINT_KIND_LABEL, value === 'tr' ? TR_ENDPOINT_KIND_LABEL : EN_ENDPOINT_KIND_LABEL);
  Object.assign(SESSION_STATUS_LABEL, value === 'tr' ? TR_SESSION_STATUS_LABEL : EN_SESSION_STATUS_LABEL);
  Object.assign(CREDIT_REASON_LABEL, value === 'tr' ? TR_CREDIT_REASON_LABEL : EN_CREDIT_REASON_LABEL);
}

export function sessionTone(status: string): 'neutral' | 'success' | 'warning' | 'danger' | 'primary' {
  switch (status) {
    case 'running':
      return 'primary';
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'stopped':
    case 'cancelled':
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * Turkce guvenli URL parcasi: "Leds Play" -> "leds-play".
 * Kategori sayfalari (/katalog/:kategori) URL kimligini bundan alir.
 */
export function slugifyTr(s: string): string {
  const map: Record<string, string> = { ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u' };
  return s
    .toLocaleLowerCase('tr')
    .replace(/[çğıöşü]/g, (ch) => map[ch] ?? ch)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Katalog adlari marka/urun ailesidir; arayuz dilinin harf donusumune tabi
 * tutulmaz. Ozellikle Turkce buyuk `İ`, Ingilizce `Immersive` adini bozar.
 * API'de eski veya hatali kasa sahip bir deger bulunsa da kullaniciya tek
 * kanonik yazim gosterilir.
 */
export function canonicalCatalogName(name: string): string {
  const compact = name.trim().replace(/\s+/g, ' ');
  const key = compact
    .normalize('NFKD')
    .replace(/\u0307/g, '')
    .toLocaleLowerCase('en-US');

  const names: Record<string, string> = {
    'led oyunlar': 'Leds Play',
    'led ekran oyunlari': 'Leds Play',
    'etkilesimli led oyunlari': 'Leds Play',
    'leds play': 'Leds Play',
    'vr oyunlar': 'VR Deneyimleri',
    'vr deneyimleri': 'VR Deneyimleri',
    'immersive digital art': 'Immersive Digital Art',
    'etkilesimli dijital sanat': 'Immersive Digital Art',
    'ar projeleri': 'AR Deneyimleri',
    'ar deneyimleri': 'AR Deneyimleri',
    servisler: 'Servisler',
  };

  return names[key] ?? compact;
}

/** Ingilizce marka terimlerinin telaffuz ve CSS harf kurali Turkceye donmez. */
export function catalogNameLanguage(name: string): 'en' | undefined {
  return canonicalCatalogName(name) === 'Immersive Digital Art' ? 'en' : undefined;
}

/**
 * Musteri vitrinlerinde AR proje isi, hazir katalog deneyimlerinden sonra
 * sunulur. Diger kataloglar kendi alfabetik duzenini korur; AR her dilde sona
 * gider.
 */
export function compareCatalogDisplayOrder(a: string, b: string, locale = 'tr-TR'): number {
  const left = canonicalCatalogName(a);
  const right = canonicalCatalogName(b);
  const leftIsAr = left === 'AR Deneyimleri';
  const rightIsAr = right === 'AR Deneyimleri';
  if (leftIsAr !== rightIsAr) return leftIsAr ? 1 : -1;
  return left.localeCompare(right, locale);
}
